import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

export type ThemeMode = 'dark' | 'navy' | 'light';

export const THEME_OPTIONS: { mode: ThemeMode; label: string }[] = [
  { mode: 'dark', label: 'Slate Dark' },
  { mode: 'navy', label: 'Navy Blue' },
  { mode: 'light', label: 'White' },
];

// Single source of truth for the header row treatment (background/text/
// border) shared by plain MUI Tables (via each theme's MuiTableHead
// styleOverrides below) and MUI X DataGrid (which is a separate component
// library — theme.components overrides for MuiTableHead never reach its
// `.MuiDataGrid-columnHeader*` classes, so DataGrid instances must apply
// these values directly via sx; see dataGridHeaderSx()).
export const TABLE_HEADER_COLORS: Record<ThemeMode, { bg: string; color: string; border: string }> = {
  dark: { bg: '#0d1117', color: '#4fc3f7', border: '#2d3748' },
  navy: { bg: '#060f18', color: '#40ace2', border: '#273849' },
  light: { bg: '#f9fafb', color: '#6b7280', border: '#e2e5e9' },
};

// sx object for a <DataGrid sx={...}> to match the app's plain-Table header
// styling for the given theme mode.
export const dataGridHeaderSx = (mode: ThemeMode) => {
  const { bg, color, border } = TABLE_HEADER_COLORS[mode];
  return {
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: bg,
      borderBottom: `2px solid ${border}`,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      color,
      fontWeight: 600,
    },
  };
};

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 700 },
  h2: { fontWeight: 700 },
  h3: { fontWeight: 600 },
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
};

// Overrides MUI's own default Toolbar mixin (56px / 64px at the sm+
// breakpoint) so the AppBar lines up with the sidebar header's height.
// Set via the theme's component defaults rather than instance-level sx —
// sx on a single Toolbar competes with MUI's own responsive styleOverrides
// at equal specificity and can lose the cascade; overriding the component
// default here replaces it outright instead of fighting it.
const sharedToolbarOverride: ThemeOptions['components'] = {
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: 66,
        '@media (min-width:600px)': {
          minHeight: 66,
        },
      },
    },
  },
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4fc3f7',
      light: '#81d4fa',
      dark: '#0288d1',
      contrastText: '#0f1117',
    },
    secondary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    background: {
      default: '#0f1117',
      paper: '#1a1f2e',
    },
    text: {
      primary: '#e2e8f0',
      secondary: '#94a3b8',
    },
    divider: '#2d3748',
    action: {
      hover: 'rgba(79, 195, 247, 0.08)',
      selected: 'rgba(79, 195, 247, 0.16)',
    },
  },
  typography: sharedTypography,
  shape: {
    borderRadius: 8,
  },
  components: {
    ...sharedToolbarOverride,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0d1117',
          backgroundImage: 'none',
          borderBottom: '1px solid #2d3748',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#131720',
          backgroundImage: 'none',
          borderRight: '1px solid #2d3748',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: TABLE_HEADER_COLORS.dark.bg,
            color: TABLE_HEADER_COLORS.dark.color,
            fontWeight: 600,
            borderBottom: `2px solid ${TABLE_HEADER_COLORS.dark.border}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(79, 195, 247, 0.05)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #2d3748',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#4fc3f7',
          color: '#0f1117',
          '&:hover': {
            backgroundColor: '#81d4fa',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 8px',
          width: 'calc(100% - 16px)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(79, 195, 247, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(79, 195, 247, 0.22)',
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#2d3748',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#2d3748',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#131720',
          border: '1px solid #2d3748',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#131720',
          border: '1px solid #2d3748',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2d3748',
          color: '#e2e8f0',
          fontSize: '0.75rem',
        },
      },
    },
  },
});

// Navy variant — built around the Morgan Rural Tech logo's brand blue (#40ace2),
// darkened into a deep steel-navy background instead of the slate/graphite of
// the default dark theme.
const navyTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#40ace2',
      light: '#71c2ea',
      dark: '#1481b8',
      contrastText: '#08121c',
    },
    secondary: {
      main: '#fdc424',
      light: '#fdcc4e',
      dark: '#da8e0b',
    },
    background: {
      default: '#08121c',
      paper: '#121e2b',
    },
    text: {
      primary: '#dee5ed',
      secondary: '#8b9db1',
    },
    divider: '#273849',
    action: {
      hover: 'rgba(64, 172, 226, 0.08)',
      selected: 'rgba(64, 172, 226, 0.16)',
    },
  },
  typography: sharedTypography,
  shape: {
    borderRadius: 8,
  },
  components: {
    ...sharedToolbarOverride,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#060f18',
          backgroundImage: 'none',
          borderBottom: '1px solid #273849',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0d1926',
          backgroundImage: 'none',
          borderRight: '1px solid #273849',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#121e2b',
          border: '1px solid #273849',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#121e2b',
          border: '1px solid #273849',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: TABLE_HEADER_COLORS.navy.bg,
            color: TABLE_HEADER_COLORS.navy.color,
            fontWeight: 600,
            borderBottom: `2px solid ${TABLE_HEADER_COLORS.navy.border}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(64, 172, 226, 0.05)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #273849',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#40ace2',
          color: '#08121c',
          '&:hover': {
            backgroundColor: '#71c2ea',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 8px',
          width: 'calc(100% - 16px)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(64, 172, 226, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(64, 172, 226, 0.22)',
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#273849',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#273849',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0d1926',
          border: '1px solid #273849',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0d1926',
          border: '1px solid #273849',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#273849',
          color: '#dee5ed',
          fontSize: '0.75rem',
        },
      },
    },
  },
});

// Light variant — Cloudflare-style white surfaces/borders, with the Morgan
// Rural Tech brand blue as the primary/selected-state accent (deepened to
// #0e6a96 for AA contrast on white — see the MuiTypography note below) and
// brand gold as secondary, instead of Cloudflare's own orange.
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0e6a96',
      light: '#40ace2',
      dark: '#1481b8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8a5c0e',
      light: '#fdc424',
      dark: '#6b4709',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f6f7f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1c1c28',
      secondary: '#6b7280',
    },
    divider: '#e2e5e9',
    action: {
      hover: 'rgba(14, 106, 150, 0.06)',
      selected: 'rgba(14, 106, 150, 0.12)',
    },
  },
  typography: sharedTypography,
  shape: {
    borderRadius: 8,
  },
  components: {
    ...sharedToolbarOverride,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          borderBottom: '1px solid #e2e5e9',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          borderRight: '1px solid #e2e5e9',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e5e9',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e5e9',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: TABLE_HEADER_COLORS.light.bg,
            color: TABLE_HEADER_COLORS.light.color,
            fontWeight: 600,
            borderBottom: `2px solid ${TABLE_HEADER_COLORS.light.border}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(14, 106, 150, 0.04)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e2e5e9',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#0e6a96',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#0a4f70',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 8px',
          width: 'calc(100% - 16px)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(14, 106, 150, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(14, 106, 150, 0.18)',
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e2e5e9',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: '#e2e5e9',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          border: '1px solid #e2e5e9',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          border: '1px solid #e2e5e9',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1c1c28',
          color: '#f6f7f8',
          fontSize: '0.75rem',
        },
      },
    },
    // Heading colour split, using darkened tints of the Morgan Rural Tech
    // brand blue/gold (#40ace2 / #fdc424 — see navyTheme above) rather than
    // the raw brand hexes: both fail WCAG AA on white at these weights/sizes
    // (raw gold is ~1.6:1), so these are deepened to ~5.8-6:1 for legibility.
    // Page-level titles (h4/h5) get blue; component/card titles (h6,
    // subtitle1) get gold, giving a two-tone hierarchy. subtitle2 and body
    // text stay neutral — it's used too densely (labels, table cells) for a
    // brand tint to read as anything but noise.
    MuiTypography: {
      styleOverrides: {
        h4: { color: '#0e6a96' },
        h5: { color: '#0e6a96' },
        h6: { color: '#8a5c0e' },
        subtitle1: { color: '#8a5c0e' },
      },
    },
  },
});

export const themes: Record<ThemeMode, Theme> = {
  dark: darkTheme,
  navy: navyTheme,
  light: lightTheme,
};
